import { createSlice } from "@reduxjs/toolkit";
import { customProfilesApi } from "Services/CustomProfilesService";

export const initialState = {
    completedStepIdList: [],
    importType: null,
    file: {
        name: "",
        type: "",
        rowCount: 0,
        headers: [], // Array of headers from the uploaded file ["First Name", "Last", "Email", "Position", "Phone Number"]
        originalHeaders: [], // Array of original headers from the uploaded file, used to restore headers if needed
        rows: [],
    },
    fields: [], //[{ fieldName: string, header: string, customField: boolean, isRequired?: boolean }]
    errorFields: null,
    processedData: [],
    removedProcessedData: null, //{ index: number, data: object }
    loading: false,
    nextDisabled: false,
};

const importDatabaseStepperSlice = createSlice({
    name: "import-database-stepper",
    initialState,
    reducers: {
        addStepIdToCompletedList: (state, action) => {
            const stepId = action.payload;
            if (!state.completedStepIdList.includes(stepId)) {
                state.completedStepIdList.push(action.payload);
            }
        },
        removeStepIdToCompletedList: (state, action) => {
            state.completedStepIdList = state.completedStepIdList.filter(
                (stepId) => stepId !== action.payload
            );
        },
        setImportType: (state, action) => {
            state.importType = action.payload;
            Object.assign(state.file, initialState.file);
            state.processedData = [];
            state.removedProcessedData = null;
            state.processedData = [];
            state.removedProcessedData = null;
            state.errorFields = null;
        },
        setFields: (state, action) => {
            const fieldDefinitions = action.payload ?? [];
            state.fields = fieldDefinitions.map((fieldDef) => ({
                fieldName: fieldDef.name,
                isRequired: fieldDef.isRequired,
                header: null,
                customField: false,
            }));
        },
        setFile: (state, action) => {
            const {
                name,
                type,
                rowCount,
                headers = [],
                rows = [],
            } = action.payload;
            state.file = {
                name,
                type,
                rowCount,
                headers,
                originalHeaders: [...headers],
                rows,
            };
            state.fields = state.fields.map((field) => ({
                ...field,
                fieldName: field.fieldName,
                header: null,
            }));
            state.processedData = [];
            state.removedProcessedData = null;
            state.processedData = [];
            state.removedProcessedData = null;
            state.errorFields = null;
        },
        setHeaders: (state, action) => {
            state.file.headers = action.payload;
            state.fields = action.payload.map((header) => ({
                fieldName: header,
                header: null,
                customField: false,
            }));
        },
        addField: (state, action) => {
            state.fields.push({
                fieldName: action.payload,
                header: null,
                customField: true,
            });
        },
        editFieldName: (state, action) => {
            const { previousFieldName, newFieldName } = action.payload;
            const fieldIndex = state.fields.findIndex(
                (field) => field.fieldName === previousFieldName
            );

            if (fieldIndex !== -1) {
                state.fields[fieldIndex].fieldName = newFieldName;
            }
        },
        removeField: (state, action) => {
            const fieldIndex = state.fields.findIndex(
                (field) => field.fieldName === action.payload
            );

            if (fieldIndex !== -1) {
                const deletedField = state.fields.splice(fieldIndex, 1);
                if (deletedField.length > 0 && deletedField[0].header) {
                    // If the deleted field had a header, add it back to the headers list
                    state.file.headers.push(deletedField[0].header);
                }
            }
        },
        moveHeaderToField: (state, action) => {
            const { header, fieldName } = action.payload;
            const fieldIndex = state.fields.findIndex(
                (field) => field.fieldName === fieldName
            );

            if (fieldIndex !== -1) {
                //If the destination field is detected, set the header to that field
                state.fields[fieldIndex].header = header;
                state.file.headers = state.file.headers.filter(
                    (h) => h !== header
                );

                // Remove any error associated with this field
                if (
                    state.errorFields &&
                    state.errorFields.hasOwnProperty(fieldName)
                ) {
                    delete state.errorFields[fieldName];
                }
            } else if (!state.file.headers.includes(header)) {
                //If the destination field is not detected, put the header back to the Headers list
                state.file.headers.push(header);
            }
        },
        removeFieldHeader: (state, action) => {
            const { fieldName } = action.payload;
            const fieldIndex = state.fields.findIndex(
                (field) => field.fieldName === fieldName
            );
            if (fieldIndex !== -1) {
                state.fields[fieldIndex].header = null;
            }
        },
        processData: (state) => {
            const fields = state.fields.filter((field) => field.header);
            const { rows, originalHeaders } = state.file;

            let processedData = [];
            const removedFileRowIndexes = state.processedData
                .filter((data) => data.removed)
                .map((data) => data.fileRowIndex);

            processedData.push({
                fileRowIndex: 0,
                data: fields.map((field) => field.fieldName),
                removed: false,
            });

            const indexesPairs = {};
            fields.forEach((field) => {
                const fieldIndex = originalHeaders.indexOf(field.header);
                indexesPairs[field.fieldName] = fieldIndex;
            });

            rows.forEach((row, index) => {
                const fileRowIndex = index + 1;
                const removed = removedFileRowIndexes.includes(fileRowIndex);

                const newRow = [];
                fields.forEach((field) => {
                    newRow.push(row[indexesPairs[field.fieldName]]);
                });
                processedData.push({
                    fileRowIndex: fileRowIndex,
                    data: newRow,
                    removed,
                });
            });

            state.processedData = processedData;
        },
        removeProcessedData: (state, action) => {
            const fileRowIndex = action.payload;
            const index = state.processedData.findIndex(
                (data) => data.fileRowIndex === fileRowIndex
            );

            if (index !== -1) {
                const data = state.processedData[index];
                data.removed = true;
                state.removedProcessedData = {
                    index,
                    data: data,
                };
            }
        },
        undoRemovedProcessedData: (state) => {
            const removedData = state.removedProcessedData;
            if (removedData) {
                const data = state.processedData.find(
                    (data) =>
                        data.fileRowIndex === removedData.data.fileRowIndex
                );
                if (data) {
                    data.removed = false;
                }
                state.removedProcessedData = null;
            }
        },
        setErrorFields: (state, action) => {
            state.errorFields = action.payload;
        },
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setNextDisabled: (state, action) => {
            state.nextDisabled = action.payload;
        },
        reset: (state) => {
            Object.assign(state, initialState);
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(
                customProfilesApi.endpoints.importProfiles.matchPending,
                (state) => {
                    state.loading = true;
                }
            )
            .addMatcher(
                customProfilesApi.endpoints.importProfiles.matchFulfilled,
                (state) => {
                    state.loading = false;
                }
            )
            .addMatcher(
                customProfilesApi.endpoints.importProfiles.matchRejected,
                (state) => {
                    state.loading = false;
                }
            )
            .addMatcher(
                customProfilesApi.endpoints.validateImportProfiles.matchPending,
                (state) => {
                    state.loading = true;
                }
            )
            .addMatcher(
                customProfilesApi.endpoints.validateImportProfiles
                    .matchFulfilled,
                (state) => {
                    state.loading = false;
                }
            )
            .addMatcher(
                customProfilesApi.endpoints.validateImportProfiles
                    .matchRejected,
                (state) => {
                    state.loading = false;
                }
            );
    },
});

const importDatabaseStepperState = (state) => state.importDatabaseStepperState;

export const selectCompletedStepIdList = (state) =>
    importDatabaseStepperState(state).completedStepIdList;
export const selectImportType = (state) =>
    importDatabaseStepperState(state).importType;
export const selectFile = (state) => importDatabaseStepperState(state).file;
export const selectFields = (state) => importDatabaseStepperState(state).fields;
export const selectProcessedData = (state) =>
    importDatabaseStepperState(state).processedData;
export const selectLoading = (state) =>
    importDatabaseStepperState(state).loading;
export const selectNextDisabled = (state) =>
    importDatabaseStepperState(state).nextDisabled;
export const selectErrorFields = (state) =>
    importDatabaseStepperState(state).errorFields;

export const {
    addStepIdToCompletedList,
    removeStepIdToCompletedList,
    setImportType,
    setFields,
    setFile,
    addField,
    editFieldName,
    removeField,
    moveHeaderToField,
    removeFieldHeader,
    processData,
    removeProcessedData,
    undoRemovedProcessedData,
    setErrorFields,
    setLoading,
    setNextDisabled,
    reset,
} = importDatabaseStepperSlice.actions;

export default importDatabaseStepperSlice.reducer;
